import { forwardRef, type ReactNode, type SVGProps } from "react";

/**
 * Scrubmee's own small icon alphabet. These intentionally use a soft, hand-drawn
 * stroke with rounded joins instead of a third-party product-icon set.
 */
export type ScrubmeeIconProps = SVGProps<SVGSVGElement> & {
  size?: number | string;
};

const paths: Record<string, ReactNode> = {
  home: (
    <>
      <path d="M3.5 10.5 12 3l8.5 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-14A1.5 1.5 0 0 1 3.5 19z" />
      <path d="M9 21v-6h6v6" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7M3 12h18M10 12v2h4v-2" />
    </>
  ),
  wallet: (
    <>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4H18a2 2 0 0 1 2 2v2H6.5a2.5 2.5 0 0 0 0 5H21v5a2 2 0 0 1-2 2H6.5A2.5 2.5 0 0 1 4 17.5z" />
      <path d="M17 13h4v4h-4a2 2 0 0 1 0-4Z" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 20 6v5.8c0 4.5-3.2 7.8-8 9.2-4.8-1.4-8-4.7-8-9.2V6z" />
      <path d="m8.5 12 2.2 2.2 4.8-4.8" />
    </>
  ),
  spray: (
    <>
      <path d="M10 5h5v4h-5zM12 5V3h4" />
      <path d="M7 9h10l2 11H5z" />
      <path d="M4 6H2m3-2L3.5 2.5M4.5 8.5 2.5 10" />
      <path d="M10 13h4" />
    </>
  ),
  check: <path d="m5 12 4.2 4.2L19 6.5" />,
  listChecks: (
    <>
      <path d="M4 6h2m3 0h12M4 12h2m3 0h12M4 18h2m3 0h12" />
      <path d="m8 6 .8.8L10 5.6m-2 6.4.8.8 1.2-1.2m-2 6.4.8.8 1.2-1.2" />
    </>
  ),
  smartphone: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </>
  ),
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="m14 7 5 5-5 5" />
    </>
  ),
  arrowLeft: (
    <>
      <path d="M19 12H5" />
      <path d="m10 7-5 5 5 5" />
    </>
  ),
  arrowUpRight: (
    <>
      <path d="M6 18 18 6" />
      <path d="M9 6h9v9" />
    </>
  ),
  arrowDownLeft: (
    <>
      <path d="m18 6-12 12" />
      <path d="M15 18H6V9" />
    </>
  ),
  map: (
    <>
      <path d="m4 6 6-3 5 3 5-3v15l-5 3-5-3-6 3z" />
      <path d="M10 3v15M15 6v15" />
    </>
  ),
  pin: (
    <>
      <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  bell: (
    <>
      <path d="M18 10a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 21c.8-4 3.1-6 7-6s6.2 2 7 6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20c.5-3.4 2.3-5.2 5.5-5.2 1.4 0 2.6.3 3.5 1M16 5a3 3 0 0 1 0 6M16.5 14.8c2.3 0 3.6 1.5 4 4.2" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20c.5-3.4 2.3-5.2 5.5-5.2 1.4 0 2.6.3 3.5 1M18 8v6M15 11h6" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.5 2.5-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5v.2h-3.5v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1L5.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H4v-3.5h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1L7.8 5l.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.5V3.7h3.5v.2a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1 2.5 2.5-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.1V14h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </>
  ),
  alert: (
    <>
      <path d="m12 3 9 17H3z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  layout: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 9v12" />
    </>
  ),
  trend: (
    <>
      <path d="m4 17 6-6 4 4 6-7" />
      <path d="M15 8h5v5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  copy: (
    <>
      <rect x="8" y="8" width="11" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h2" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h4l1.5-2h5L16 8h4v11H4z" />
      <circle cx="12" cy="13.5" r="3" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="m15 15 5 5" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 7v5h-5" />
      <path d="M18.5 12a6.5 6.5 0 1 1-1.6-4.3L20 10" />
    </>
  ),
  play: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m10 8 5 4-5 4z" />
    </>
  ),
  banknote: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9h.01M18 15h.01" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8" cy="9" r="1.5" />
      <path d="m4 18 5-5 3 3 3-3 5 5" />
      <path d="M19 2v4M17 4h4" />
    </>
  ),
  pencil: (
    <>
      <path d="m4 20 4-1 11-11-3-3L5 16z" />
      <path d="m14 6 3 3" />
    </>
  ),
  trash: (
    <>
      <path d="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" />
    </>
  ),
  bed: (
    <>
      <path d="M4 19V9h16v10M2 19h20M7 12h4v3H7zM4 19v2M20 19v2" />
    </>
  ),
  bath: (
    <>
      <path d="M4 12h16v4a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3zM7 12V8a2 2 0 0 1 4 0v1" />
      <path d="M3 21v-2M21 21v-2" />
    </>
  ),
};

function makeIcon(name: keyof typeof paths) {
  return forwardRef<SVGSVGElement, ScrubmeeIconProps>(function Icon(
    { className, size, ...props },
    ref,
  ) {
    return (
      <svg
        ref={ref}
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...props}
      >
        {paths[name]}
      </svg>
    );
  });
}

export const Home = makeIcon("home");
export const Briefcase = makeIcon("briefcase");
export const Wallet = makeIcon("wallet");
export const ShieldCheck = makeIcon("shield");
export const ListChecks = makeIcon("listChecks");
export const Smartphone = makeIcon("smartphone");
export const Eye = makeIcon("eye");
export const ReceiptText = makeIcon("receipt");
export const SprayCan = makeIcon("spray");
export const CheckCircle2 = makeIcon("check");
export const Check = CheckCircle2;
export const ChevronRight = makeIcon("arrowRight");
export const ArrowRight = ChevronRight;
export const ArrowLeft = makeIcon("arrowLeft");
export const ArrowUpRight = makeIcon("arrowUpRight");
export const ArrowDownLeft = makeIcon("arrowDownLeft");
export const Map = makeIcon("map");
export const MapPin = makeIcon("pin");
export const Clock = makeIcon("clock");
export const Bell = makeIcon("bell");
export const User = makeIcon("user");
export const Users = makeIcon("users");
export const UserPlus = makeIcon("userPlus");
export const Settings = makeIcon("settings");
export const AlertTriangle = makeIcon("alert");
export const AlertCircle = AlertTriangle;
export const LayoutDashboard = makeIcon("layout");
export const TrendingUp = makeIcon("trend");
export const Plus = makeIcon("plus");
export const X = makeIcon("close");
export const XCircle = X;
export const Menu = makeIcon("menu");
export const ChevronDown = makeIcon("chevronDown");
export const Copy = makeIcon("copy");
export const FileText = makeIcon("file");
export const Camera = makeIcon("camera");
export const Search = makeIcon("search");
export const Lock = makeIcon("lock");
export const Mail = makeIcon("mail");
export const MailCheck = Mail;
export const RefreshCw = makeIcon("refresh");
export const PlayCircle = makeIcon("play");
export const Banknote = makeIcon("banknote");
export const ImagePlus = makeIcon("image");
export const Pencil = makeIcon("pencil");
export const Trash2 = makeIcon("trash");
export const BedDouble = makeIcon("bed");
export const Bath = makeIcon("bath");
export const Save = CheckCircle2;
export const Loader2 = makeIcon("refresh");
export const LogOut = ArrowRight;
export const ChevronDownIcon = ChevronDown;
export const ChevronUpIcon = ChevronDown;
export const ChevronRightIcon = ChevronRight;
export const XIcon = X;
export const CheckIcon = CheckCircle2;
export const CircleIcon = CheckCircle2;
export const CircleCheckIcon = CheckCircle2;
export const InfoIcon = AlertCircle;
export const Loader2Icon = Loader2;
export const OctagonXIcon = X;
export const TriangleAlertIcon = AlertTriangle;
